import { z } from "zod";
/**
 * MCP (Model Context Protocol) endpoint schemas — the JSON-RPC 2.0 envelope
 * and per-tool input shapes served at `POST /api/mcp`.
 *
 * The endpoint is a minimal, stateless Streamable-HTTP MCP server (see
 * ADR-0091): external Claude surfaces (Claude Code, Claude Desktop, any MCP
 * client that can send an `Authorization` header) query and feed the ideation
 * knowledge graph using an ADR-0080 Personal Access Token. Tool *inputs* are
 * validated with these Zod schemas; the JSON Schema literals advertised by
 * `tools/list` live next to the tool registry in
 * `packages/api/src/services/mcp.service.ts` and must stay in sync.
 */
/** JSON-RPC 2.0 request id — string or number (null ids are not accepted). */
export const McpRequestIdSchema = z.union([z.string(), z.number()]);
/**
 * A single JSON-RPC 2.0 message. `id` absent ⇒ notification (no response body,
 * HTTP 202). Batch arrays are rejected by the route (removed in MCP 2025-06-18).
 */
export const McpRpcMessageSchema = z.object({
    jsonrpc: z.literal("2.0"),
    id: McpRequestIdSchema.optional(),
    method: z.string().min(1).max(200),
    params: z.record(z.string(), z.unknown()).optional(),
});
/** MCP protocol versions this server accepts (newest last). */
export const MCP_SUPPORTED_PROTOCOL_VERSIONS = ["2024-11-05", "2025-03-26", "2025-06-18"];
// ─── Tool inputs ─────────────────────────────────────────────────────────────
/** `search_knowledge` — semantic search across the visibility-filtered corpus. */
export const McpSearchKnowledgeInputSchema = z.object({
    query: z.string().min(1).max(2_000),
});
/** `get_node_context` — assembled context bundle for one node. */
export const McpGetNodeContextInputSchema = z.object({
    nodeId: z.string().min(1).max(100),
});
/** `list_maps` — no inputs; kept as an object for forward-compatibility. */
export const McpListMapsInputSchema = z.object({});
/**
 * `capture_knowledge` — feed content into the extraction pipeline. Exactly one
 * of `text` (plain prose, ≤200k chars, mirrors `ExtractionCreateSchema.text`)
 * or `rawSource` (a conversation export, ≤2 MB, normalised by the ingestion
 * adapter named in `format`) must be provided — the same one-of contract as
 * `ExtractionCreateSchema`. `mapId` optionally targets a map for accepted
 * nodes on apply.
 */
/** Optional parking-lot note alongside a graph capture (B4). */
const McpAlsoWriteNoteFields = {
    alsoWriteNote: z.boolean().optional().default(false),
    noteTitle: z.string().min(1).max(300).nullable().default(null),
};
export const McpCaptureKnowledgeInputSchema = z
    .object({
    text: z.string().min(1).max(200_000).nullable().default(null),
    rawSource: z.string().min(1).max(2_000_000).nullable().default(null),
    format: z.enum(["markdown", "claude-ai-json", "claude-code-jsonl"]).optional(),
    mapId: z.string().min(1).max(100).nullable().default(null),
    ...McpAlsoWriteNoteFields,
})
    .refine((v) => [Boolean(v.text), Boolean(v.rawSource)].filter(Boolean).length === 1, {
    message: "Provide exactly one of text or rawSource",
})
    .refine((v) => !v.rawSource || Boolean(v.format), {
    message: "format is required when rawSource is provided",
});
/**
 * `capture_decision` — structured decision note for higher-quality extraction
 * than a raw chat dump. Always targets a map (`mapId` required). The tool
 * formats fields into plain text and reuses the same reviewed-diff pipeline
 * as `capture_knowledge`.
 */
export const McpCaptureDecisionInputSchema = z.object({
    decision: z.string().min(1).max(4_000),
    rationale: z.string().max(20_000).nullable().default(null),
    owners: z.array(z.string().min(1).max(200)).max(20).nullable().default(null),
    context: z.string().max(20_000).nullable().default(null),
    mapId: z.string().min(1).max(100),
    ...McpAlsoWriteNoteFields,
});
/**
 * `capture_fact` — propose one Subject FACT for staff review. Never writes
 * `context.facts`. Requires PAT scope ideation:write (Clerk sessions pass
 * the same gate as capture_knowledge).
 */
export const McpCaptureFactInputSchema = z.object({
    subjectId: z.string().uuid(),
    key: z
        .string()
        .min(1)
        .max(80)
        .regex(/^[a-z0-9][a-z0-9_]*$/, "key must be lower_snake_case"),
    value: z.string().min(1).max(400),
    unit: z.string().max(40).nullable().optional(),
    source: z.string().min(1).max(400),
    asOf: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}(T[\d:.]+(Z|[+-]\d{2}:\d{2}))?$/, "asOf must be YYYY-MM-DD or an ISO-8601 timestamp"),
    note: z.string().max(1_000).nullable().optional(),
    rationale: z.string().max(400).nullable().optional(),
});
// ─── Structured graph tools (ADR-0109) ───────────────────────────────────────
//
// The capture tools above are the REVIEWED path: prose in, confidence-banded
// proposals out, a human applies them. The tools below are the STRUCTURED path:
// what you send is what is stored, immediately, verbatim, with provenance
// required on every value. Both write to the same graph; neither is a
// replacement for the other (rationale and narrative belong in capture,
// part numbers and costs belong here).
//
// Every write tool accepts `dryRun` and every write is additive or a
// supersede — there is no destructive delete on this surface.
/** A fact as supplied by a caller — `recordedBy`/`recordedAt` are server-set. */
export const McpFactInputSchema = z.object({
    key: z
        .string()
        .min(1)
        .max(80)
        .regex(/^[a-z0-9][a-z0-9_]*$/, "key must be lower_snake_case"),
    value: z.string().min(1).max(400),
    unit: z.string().max(40).nullable().default(null),
    source: z.string().min(1).max(400),
    asOf: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}(T[\d:.]+(Z|[+-]\d{2}:\d{2}))?$/, "asOf must be YYYY-MM-DD or an ISO-8601 timestamp"),
    note: z.string().max(1_000).nullable().default(null),
});
/**
 * `upsert_node` — create or update one node deterministically.
 *
 * Identity resolution is exact, never fuzzy: `nodeId` when known, otherwise an
 * exact case-insensitive `label` + `kind` match. An ambiguous label is an error
 * listing the candidates — the tool never guesses which node you meant.
 */
export const McpUpsertNodeInputSchema = z
    .object({
    nodeId: z.string().min(1).max(100).nullable().default(null),
    label: z.string().min(1).max(300).nullable().default(null),
    kind: z.string().min(1).max(60).nullable().default(null),
    mapId: z.string().min(1).max(100).nullable().default(null),
    summary: z.string().max(600).nullable().default(null),
    /** Replaces the body outright (previous text stays in the mutation ledger). */
    body: z.string().max(100_000).nullable().default(null),
    /** Appends a section to the body instead of replacing it. */
    bodyAppend: z.string().max(100_000).nullable().default(null),
    aliases: z.array(z.string().min(1).max(300)).max(50).nullable().default(null),
    facts: z.array(McpFactInputSchema).max(100).nullable().default(null),
    /** Provenance for the write itself — recorded on the node's sourceRefs. */
    source: z.string().min(1).max(400),
    dryRun: z.boolean().default(false),
})
    .refine((v) => Boolean(v.nodeId) || (Boolean(v.label) && Boolean(v.kind)), {
    message: "Provide nodeId, or both label and kind",
})
    .refine((v) => !(v.body && v.bodyAppend), {
    message: "Provide at most one of body or bodyAppend",
});
/** `upsert_edge` — idempotent typed relationship between two existing nodes. */
export const McpUpsertEdgeInputSchema = z.object({
    from: z.string().min(1).max(100),
    to: z.string().min(1).max(100),
    rel: z.string().min(1).max(60),
    note: z.string().max(2_000).nullable().default(null),
    source: z.string().min(1).max(400),
    dryRun: z.boolean().default(false),
});
/**
 * `supersede_node` — mark a node no longer current.
 *
 * Nothing is deleted: the node keeps its body and edges, gains a supersede
 * marker that every read path leads with, and (when a successor is named) a
 * `supersedes` edge from the successor to it.
 */
export const McpSupersedeNodeInputSchema = z
    .object({
    nodeId: z.string().min(1).max(100),
    supersededByNodeId: z.string().min(1).max(100).nullable().default(null),
    reason: z.string().min(1).max(2_000),
    asOf: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}(T[\d:.]+(Z|[+-]\d{2}:\d{2}))?$/, "asOf must be YYYY-MM-DD or an ISO-8601 timestamp"),
    dryRun: z.boolean().default(false),
})
    .refine((v) => v.nodeId !== v.supersededByNodeId, {
    message: "A node cannot supersede itself",
});
/**
 * `query_nodes` — structural, exhaustive, filterable.
 *
 * Unlike `search_knowledge` (ranked and relevance-trimmed), this returns EVERY
 * node matching the filters within the caller's visibility, and states the
 * total so a truncated page can never be mistaken for a complete answer.
 */
export const McpQueryNodesInputSchema = z
    .object({
    kinds: z.array(z.string().min(1).max(60)).max(20).nullable().default(null),
    mapId: z.string().min(1).max(100).nullable().default(null),
    labelContains: z.string().min(1).max(300).nullable().default(null),
    factKey: z
        .string()
        .min(1)
        .max(80)
        .regex(/^[a-z0-9][a-z0-9_]*$/)
        .nullable()
        .default(null),
    factValueContains: z.string().min(1).max(400).nullable().default(null),
    /** Numeric comparison against the leading number of `factKey`'s value. */
    factNumericOp: z.enum(["lt", "lte", "gt", "gte", "eq"]).nullable().default(null),
    factNumericValue: z.number().nullable().default(null),
    includeSuperseded: z.boolean().default(false),
    limit: z.number().int().min(1).max(500).default(100),
    offset: z.number().int().min(0).default(0),
})
    .refine((v) => !v.factValueContains || Boolean(v.factKey), {
    message: "factValueContains requires factKey",
})
    .refine((v) => (v.factNumericOp == null) === (v.factNumericValue == null), {
    message: "factNumericOp and factNumericValue must be provided together",
})
    .refine((v) => v.factNumericOp == null || Boolean(v.factKey), {
    message: "factNumericOp requires factKey",
});
/** `get_schema` — node kinds, relationship types, and fact keys in use. */
export const McpGetSchemaInputSchema = z.object({
    /** Include per-kind / per-key usage counts (default true). */
    includeCounts: z.boolean().default(true),
});
// ─── Map structure / journey tools (membership display; distinct from ADR-0109) ─
/** `create_map` — create an empty or pre-membered knowledge map. */
export const McpCreateMapInputSchema = z.object({
    title: z.string().min(1).max(200),
    kind: z.string().min(1).max(60).default("graph"),
    instructions: z.string().max(4000).default(""),
    visibility: z.enum(["tenant", "private"]).default("tenant"),
});
/** `archive_map` — soft-archive a map the caller owns. */
export const McpArchiveMapInputSchema = z.object({
    mapId: z.string().min(1).max(100),
});
/**
 * `get_map` — map canvas view. `includeJourney` reveals membership-display
 * superseded nodes (canvas journey clutter) — NOT the same as
 * `query_nodes.includeSuperseded` (node currency / ADR-0109).
 */
export const McpGetMapInputSchema = z.object({
    mapId: z.string().min(1).max(100),
    includeJourney: z.boolean().default(false),
});
/**
 * `list_map_links` — peer map↔map links (ADR-0113). Prefer `get_map` when you
 * also need nodes/edges; use this for a links-only read.
 */
export const McpListMapLinksInputSchema = z.object({
    mapId: z.string().min(1).max(100),
    /** Include inbound links (default true). Outbound-only when false. */
    inbound: z.boolean().default(true),
});
/**
 * `create_map_link` — typed peer link from `mapId` → `toMapId` (not hub hierarchy).
 */
export const McpCreateMapLinkInputSchema = z.object({
    mapId: z.string().min(1).max(100),
    toMapId: z.string().min(1).max(100),
    rel: z.string().min(1).max(60).default("related"),
});
/** `delete_map_link` — remove a peer map link by id (from list_map_links / get_map). */
export const McpDeleteMapLinkInputSchema = z.object({
    linkId: z.string().min(1).max(100),
});
/** `set_member_display` — per-map canvas visibility; also places a non-member. */
export const McpSetMemberDisplayInputSchema = z.object({
    mapId: z.string().min(1).max(100),
    nodeId: z.string().min(1).max(100),
    display: z.enum(["shown", "superseded", "removed"]),
});
/**
 * `set_map_membership` — add/remove an existing node on a map without rewriting
 * the node identity (pure membership join; distinct from upsert_node + mapId).
 */
export const McpSetMapMembershipInputSchema = z.object({
    mapId: z.string().min(1).max(100),
    nodeId: z.string().min(1).max(100),
    member: z.boolean(),
});
/**
 * `move_map_membership` — reassign a node from one map to another (add-then-remove
 * so the node is never briefly orphaned). Distinct from set_map_membership which
 * only toggles a single map.
 */
export const McpMoveMapMembershipInputSchema = z
    .object({
    nodeId: z.string().min(1).max(100),
    fromMapId: z.string().min(1).max(100),
    toMapId: z.string().min(1).max(100),
})
    .refine((v) => v.fromMapId !== v.toMapId, {
    message: "fromMapId and toMapId must differ",
});
/**
 * ChatGPT-safe alias of set_map_membership. ChatGPT's MCP ingest silently
 * drops tools whose names contain "membership" (set_member_display survives).
 * `action` is an enum so the schema has no required boolean.
 */
export const McpPlaceNodeOnMapInputSchema = z.object({
    mapId: z.string().min(1).max(100),
    nodeId: z.string().min(1).max(100),
    action: z.enum(["add", "remove"]),
});
/** ChatGPT ingest also dropped `place_node_on_map` / `move_node_between_maps`. */
export const McpMapNodePairInputSchema = z.object({
    mapId: z.string().min(1).max(100),
    nodeId: z.string().min(1).max(100),
});
/**
 * ChatGPT-safe alias of move_map_membership (same fields, different tool name).
 * Re-declared (not `= McpMoveMapMembershipInputSchema`) so knip doesn't flag a
 * duplicate export of the same Zod object under two names.
 */
export const McpMoveNodeBetweenMapsInputSchema = z
    .object({
    nodeId: z.string().min(1).max(100),
    fromMapId: z.string().min(1).max(100),
    toMapId: z.string().min(1).max(100),
})
    .refine((v) => v.fromMapId !== v.toMapId, {
    message: "fromMapId and toMapId must differ",
});
/**
 * `set_node_parent` — nest a member under another member on the same map
 * (`parentNodeId: null` = top-level). Both nodes must already be members.
 */
export const McpSetNodeParentInputSchema = z.object({
    mapId: z.string().min(1).max(100),
    nodeId: z.string().min(1).max(100),
    parentNodeId: z.string().min(1).max(100).nullable().default(null),
});
/** `archive_edge` — soft-archive a typed edge (path retained). */
export const McpArchiveEdgeInputSchema = z.object({
    edgeId: z.string().min(1).max(100),
});
/** `unarchive_edge` — restore a soft-archived edge to the canvas. */
export const McpUnarchiveEdgeInputSchema = z.object({
    edgeId: z.string().min(1).max(100),
});
/** `update_map` — rename / patch map metadata the caller can edit. */
export const McpUpdateMapInputSchema = z
    .object({
    mapId: z.string().min(1).max(100),
    title: z.string().min(1).max(200).optional(),
    kind: z.string().min(1).max(60).optional(),
    instructions: z.string().max(4000).optional(),
    visibility: z.enum(["tenant", "private"]).optional(),
})
    .refine((v) => v.title !== undefined ||
    v.kind !== undefined ||
    v.instructions !== undefined ||
    v.visibility !== undefined, { message: "Provide at least one of title, kind, instructions, visibility" });
/**
 * `update_node` — rename / patch an existing node by id (not identity-match).
 * Distinct from `upsert_node`, which keeps the existing label when resolving
 * by nodeId.
 */
export const McpUpdateNodeInputSchema = z
    .object({
    nodeId: z.string().min(1).max(100),
    label: z.string().min(1).max(300).nullable().default(null),
    kind: z.string().min(1).max(60).nullable().default(null),
    aliases: z.array(z.string().min(1).max(300)).max(50).nullable().default(null),
    summary: z.string().max(600).nullable().default(null),
    body: z.string().max(100_000).nullable().default(null),
    bodyAppend: z.string().max(100_000).nullable().default(null),
    source: z.string().min(1).max(400),
    dryRun: z.boolean().default(false),
})
    .refine((v) => !(v.body && v.bodyAppend), {
    message: "Provide at most one of body or bodyAppend",
})
    .refine((v) => v.label !== null ||
    v.kind !== null ||
    v.aliases !== null ||
    v.summary !== null ||
    v.body !== null ||
    v.bodyAppend !== null, {
    message: "Provide at least one field to update (label, kind, aliases, summary, body, bodyAppend)",
});
/** `update_edge` — change rel and/or note in place (endpoints fixed). */
export const McpUpdateEdgeInputSchema = z
    .object({
    edgeId: z.string().min(1).max(100),
    rel: z.string().min(1).max(60).optional(),
    note: z.string().max(2_000).nullable().optional(),
})
    .refine((v) => v.rel !== undefined || v.note !== undefined, {
    message: "Provide at least one of rel, note",
});
// ─── Map history (ADR-0074) ────────────────────────────────────────────────
/** `list_map_commits` — git-object commit log for one map (HEAD first). */
export const McpListMapCommitsInputSchema = z.object({
    mapId: z.string().min(1).max(100),
    limit: z.number().int().min(1).max(100).default(20),
    /** ISO timestamp — commits with createdAt strictly before this (pagination). */
    before: z.string().optional(),
});
/** `get_commit_view` — one commit hydrated with member snapshots. */
export const McpGetCommitViewInputSchema = z.object({
    mapId: z.string().min(1).max(100),
    commitId: z.string().min(1).max(100),
});
/**
 * `restore_map_commit` — reset the live map (and shared node bodies) to a
 * commit. Always returns the cross-map warning. Apply is refused when other
 * maps share members unless `acknowledgeCrossMap` is true.
 */
export const McpRestoreMapCommitInputSchema = z.object({
    mapId: z.string().min(1).max(100),
    commitId: z.string().min(1).max(100),
    dryRun: z.boolean().default(false),
    acknowledgeCrossMap: z.boolean().default(false),
});
//# sourceMappingURL=mcp.js.map